import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
    Car,
    Plus,
    MoreHorizontal,
    Pencil,
    Trash2,
    Calendar as CalendarIcon,
    Clock,
    User,
    Settings,
    Loader2,
    CheckCircle,
    XCircle,
    Fuel,
    Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const FUEL_TYPES = [
    { value: 'electric', label: 'Électrique', icon: Zap, color: 'text-green-500' },
    { value: 'diesel', label: 'Gazole', icon: Fuel, color: 'text-amber-600' },
    { value: 'gasoline', label: 'Essence', icon: Fuel, color: 'text-blue-500' },
];

const CATEGORY_LABELS = {
    citadine: 'Citadine',
    berline: 'Berline',
    suv: 'SUV',
    utilitaire: 'Utilitaire',
};

const formatCategory = (category) => {
    if (!category) return '-';
    return CATEGORY_LABELS[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

const Vehicles = () => {
    const { api, user, isSuperAdmin, isAdmin } = useAuth();
    
    const [vehicles, setVehicles] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('calendar');
    
    // Modal states
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showVehicleModal, setShowVehicleModal] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Booking form
    const [bookingForm, setBookingForm] = useState({
        vehicle_id: '',
        start_at: '',
        end_at: '',
        purpose: '',
        company_id: '',
        notes: '',
    });
    
    // Vehicle form
    const [vehicleForm, setVehicleForm] = useState({
        name: '',
        registration_number: '',
        category: '',
        fuel_type: 'diesel',
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const promises = [
                api.get('/vehicles'),
                api.get('/vehicle-bookings'),
                api.get('/companies'),
            ];
            
            if (isSuperAdmin()) {
                promises.push(api.get('/users'));
            }
            
            const results = await Promise.all(promises);
            
            setVehicles(results[0].data);
            setBookings(results[1].data);
            setCompanies(results[2].data);
            if (results[3]) {
                setUsers(results[3].data);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBooking = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.post('/vehicle-bookings', bookingForm);
            toast.success('Réservation créée');
            setShowBookingModal(false);
            resetBookingForm();
            fetchData();
        } catch (error) {
            console.error('Failed to create booking:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelBooking = async (bookingId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) return;
        
        try {
            await api.delete(`/vehicle-bookings/${bookingId}`);
            toast.success('Réservation annulée');
            fetchData();
        } catch (error) {
            console.error('Failed to cancel booking:', error);
            toast.error('Erreur lors de l\'annulation');
        }
    };

    const handleCreateVehicle = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            if (selectedVehicle) {
                await api.patch(`/vehicles/${selectedVehicle.id}`, vehicleForm);
                toast.success('Véhicule mis à jour');
            } else {
                await api.post('/vehicles', vehicleForm);
                toast.success('Véhicule créé');
            }
            setShowVehicleModal(false);
            resetVehicleForm();
            fetchData();
        } catch (error) {
            console.error('Failed to save vehicle:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSubmitting(false);
        }
    };

    const resetBookingForm = () => {
        setBookingForm({
            vehicle_id: '',
            start_at: '',
            end_at: '',
            purpose: '',
            company_id: '',
            notes: '',
        });
    };

    const resetVehicleForm = () => {
        setSelectedVehicle(null);
        setVehicleForm({
            name: '',
            registration_number: '',
            category: '',
            fuel_type: 'diesel',
        });
    };

    const openEditVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setVehicleForm({
            name: vehicle.name,
            registration_number: vehicle.registration_number,
            category: vehicle.category || '',
            fuel_type: vehicle.fuel_type || 'diesel',
        });
        setShowVehicleModal(true);
    };

    const handleDeleteVehicle = async (vehicleId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;
        
        try {
            await api.delete(`/vehicles/${vehicleId}`);
            toast.success('Véhicule supprimé');
            fetchData();
        } catch (error) {
            console.error('Failed to delete vehicle:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const getBookingsForDate = (date) => {
        return bookings.filter(booking => {
            if (booking.status === 'cancelled') return false;
            const start = parseISO(booking.start_at);
            const end = parseISO(booking.end_at);
            return isWithinInterval(date, { start: startOfDay(start), end: endOfDay(end) });
        });
    };

    const getBookingsForVehicle = (vehicleId, date) => {
        return getBookingsForDate(date).filter(b => b.vehicle_id === vehicleId);
    };

    const isVehicleAvailable = (vehicleId, date) => {
        return getBookingsForVehicle(vehicleId, date).length === 0;
    };

    const formatDateTime = (dateString) => {
        return format(parseISO(dateString), 'dd/MM HH:mm', { locale: fr });
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading">Véhicules</h1>
                    <p className="text-muted-foreground mt-1">
                        Gérez les réservations de véhicules
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => {
                            resetBookingForm();
                            setShowBookingModal(true);
                        }}
                        className="rounded-full"
                        data-testid="create-booking-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Réserver
                    </Button>
                    {isSuperAdmin() && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                resetVehicleForm();
                                setShowVehicleModal(true);
                            }}
                            className="rounded-full"
                            data-testid="create-vehicle-btn"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Gérer véhicules
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="calendar">Calendrier</TabsTrigger>
                    <TabsTrigger value="list">Liste des réservations</TabsTrigger>
                    {isSuperAdmin() && <TabsTrigger value="vehicles">Véhicules</TabsTrigger>}
                </TabsList>

                <TabsContent value="calendar" className="mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Calendar */}
                        <Card>
                            <CardContent className="p-4">
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => date && setSelectedDate(date)}
                                    locale={fr}
                                    className="rounded-md"
                                />
                            </CardContent>
                        </Card>

                        {/* Daily view */}
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="font-heading">
                                    {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {vehicles.filter(v => v.is_active).map((vehicle) => {
                                        const vehicleBookings = getBookingsForVehicle(vehicle.id, selectedDate);
                                        const isAvailable = vehicleBookings.length === 0;
                                        
                                        return (
                                            <div
                                                key={vehicle.id}
                                                className={`p-4 rounded-xl border ${isAvailable ? 'bg-green-500/5 border-green-500/20' : 'bg-orange-500/5 border-orange-500/20'}`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAvailable ? 'bg-green-500/10' : 'bg-orange-500/10'}`}>
                                                            <Car className={`w-5 h-5 ${isAvailable ? 'text-green-500' : 'text-orange-500'}`} />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{vehicle.name}</p>
                                                            <p className="text-sm text-muted-foreground">{vehicle.registration_number}</p>
                                                        </div>
                                                    </div>
                                                    <Badge className={isAvailable ? 'bg-green-500/10 text-green-500' : 'bg-orange-500/10 text-orange-500'}>
                                                        {isAvailable ? 'Disponible' : 'Réservé'}
                                                    </Badge>
                                                </div>
                                                
                                                {vehicleBookings.length > 0 && (
                                                    <div className="mt-3 space-y-2">
                                                        {vehicleBookings.map((booking) => (
                                                            <div
                                                                key={booking.id}
                                                                className="flex items-center justify-between p-2 bg-background rounded-lg"
                                                            >
                                                                <div className="flex items-center gap-2 text-sm">
                                                                    <Clock className="w-4 h-4 text-muted-foreground" />
                                                                    <span>{formatDateTime(booking.start_at)} - {formatDateTime(booking.end_at)}</span>
                                                                    <span className="text-muted-foreground">•</span>
                                                                    <User className="w-4 h-4 text-muted-foreground" />
                                                                    <span>{booking.user_name}</span>
                                                                </div>
                                                                {(booking.user_id === user.id || isAdmin()) && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleCancelBooking(booking.id)}
                                                                        className="text-destructive hover:text-destructive"
                                                                    >
                                                                        <XCircle className="w-4 h-4" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    
                                    {vehicles.filter(v => v.is_active).length === 0 && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>Aucun véhicule disponible</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="list" className="mt-4">
                    <Card>
                        <CardContent className="p-0">
                            {bookings.length === 0 ? (
                                <div className="text-center py-12">
                                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                                    <p className="text-muted-foreground">Aucune réservation</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Véhicule</TableHead>
                                            <TableHead>Période</TableHead>
                                            <TableHead className="hidden md:table-cell">Utilisateur</TableHead>
                                            <TableHead className="hidden lg:table-cell">Motif</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="w-[50px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {bookings.map((booking) => (
                                            <TableRow key={booking.id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Car className="w-4 h-4 text-muted-foreground" />
                                                        {booking.vehicle_name}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <p>{formatDateTime(booking.start_at)}</p>
                                                    <p className="text-sm text-muted-foreground">→ {formatDateTime(booking.end_at)}</p>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    {booking.user_name}
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    {booking.purpose || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={booking.status === 'confirmed' ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}>
                                                        {booking.status === 'confirmed' ? 'Confirmée' : 'Annulée'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {booking.status === 'confirmed' && (booking.user_id === user.id || isAdmin()) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleCancelBooking(booking.id)}
                                                            className="rounded-full text-destructive hover:text-destructive"
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {isSuperAdmin() && (
                    <TabsContent value="vehicles" className="mt-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading">Parc de véhicules</CardTitle>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        resetVehicleForm();
                                        setShowVehicleModal(true);
                                    }}
                                    className="rounded-full"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter
                                </Button>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nom</TableHead>
                                            <TableHead>Immatriculation</TableHead>
                                            <TableHead>Carburant</TableHead>
                                            <TableHead>Catégorie</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="w-[50px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {vehicles.map((vehicle) => {
                                            const fuelType = FUEL_TYPES.find(f => f.value === vehicle.fuel_type) || FUEL_TYPES[1];
                                            const FuelIcon = fuelType.icon;
                                            return (
                                            <TableRow key={vehicle.id}>
                                                <TableCell className="font-medium">{vehicle.name}</TableCell>
                                                <TableCell>{vehicle.registration_number}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <FuelIcon className={`w-4 h-4 ${fuelType.color}`} />
                                                        <span className="text-sm">{fuelType.label}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{formatCategory(vehicle.category)}</TableCell>
                                                <TableCell>
                                                    <Badge className={vehicle.is_active ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}>
                                                        {vehicle.is_active ? 'Actif' : 'Inactif'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="rounded-full">
                                                                <MoreHorizontal className="w-4 h-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => openEditVehicle(vehicle)}>
                                                                <Pencil className="w-4 h-4 mr-2" />
                                                                Modifier
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDeleteVehicle(vehicle.id)}
                                                                className="text-destructive focus:text-destructive"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Supprimer
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        )})}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}
            </Tabs>

            {/* Booking Modal */}
            <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Réserver un véhicule</DialogTitle>
                        <DialogDescription>
                            Choisissez un véhicule et une période
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateBooking} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Véhicule *</Label>
                            <Select
                                value={bookingForm.vehicle_id}
                                onValueChange={(v) => setBookingForm({ ...bookingForm, vehicle_id: v })}
                                required
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {vehicles.filter(v => v.is_active).map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.name} ({v.registration_number})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Début *</Label>
                                <Input
                                    type="datetime-local"
                                    value={bookingForm.start_at}
                                    onChange={(e) => setBookingForm({ ...bookingForm, start_at: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fin *</Label>
                                <Input
                                    type="datetime-local"
                                    value={bookingForm.end_at}
                                    onChange={(e) => setBookingForm({ ...bookingForm, end_at: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Client</Label>
                            <SearchableCombobox
                                options={companies.map(c => ({ id: c.id, label: c.legal_name, sublabel: c.city || c.sector }))}
                                value={bookingForm.company_id}
                                onValueChange={(v) => setBookingForm({ ...bookingForm, company_id: v })}
                                placeholder="Rechercher un client..."
                                emptyMessage="Aucun client trouvé"
                                type="company"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Motif</Label>
                            <Input
                                value={bookingForm.purpose}
                                onChange={(e) => setBookingForm({ ...bookingForm, purpose: e.target.value })}
                                placeholder="Ex: Rendez-vous client"
                                className="rounded-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={bookingForm.notes}
                                onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                                rows={2}
                                className="rounded-lg"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowBookingModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réserver'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Vehicle Modal */}
            <Dialog open={showVehicleModal} onOpenChange={setShowVehicleModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {selectedVehicle ? 'Modifier le véhicule' : 'Nouveau véhicule'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateVehicle} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nom *</Label>
                            <Input
                                value={vehicleForm.name}
                                onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value })}
                                placeholder="Ex: Renault Clio"
                                required
                                className="rounded-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Immatriculation *</Label>
                            <Input
                                value={vehicleForm.registration_number}
                                onChange={(e) => setVehicleForm({ ...vehicleForm, registration_number: e.target.value })}
                                placeholder="AA-123-BB"
                                required
                                className="rounded-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Catégorie</Label>
                            <Select
                                value={vehicleForm.category}
                                onValueChange={(v) => setVehicleForm({ ...vehicleForm, category: v })}
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue placeholder="Optionnel" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="citadine">Citadine</SelectItem>
                                    <SelectItem value="berline">Berline</SelectItem>
                                    <SelectItem value="suv">SUV</SelectItem>
                                    <SelectItem value="utilitaire">Utilitaire</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Carburant *</Label>
                            <Select
                                value={vehicleForm.fuel_type}
                                onValueChange={(v) => setVehicleForm({ ...vehicleForm, fuel_type: v })}
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {FUEL_TYPES.map((fuel) => (
                                        <SelectItem key={fuel.value} value={fuel.value}>
                                            <div className="flex items-center gap-2">
                                                <fuel.icon className={`w-4 h-4 ${fuel.color}`} />
                                                {fuel.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowVehicleModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedVehicle ? 'Mettre à jour' : 'Créer')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Vehicles;
